/**
 * Shared TypeScript interfaces.
 * Mirrors the PHP backend's response shapes — keep these in sync if the
 * backend's serializer changes.
 */

export interface User {
  id: number;
  email: string;
}

export type OverlayCell =
  | { kind: 'solid' }
  | { kind: 'door'; open: boolean }
  | { kind: 'terrain' };

/** Per-cell wall/door overlay. Keys are "x,y" coordinate strings. */
export type MapOverlay = Record<string, OverlayCell>;

/** Set of cells that have ever been in any PC's line of sight. Keys are "x,y". */
export type ExploredCells = Record<string, true>;

// --- Initiative encounter (Savage Worlds card deck) --------------------------

export type Suit = 'spade' | 'heart' | 'diamond' | 'club';

export type Card =
  | { kind: 'rank'; rank: number; suit: Suit } // rank 2..14 (J=11,Q=12,K=13,A=14)
  | { kind: 'joker'; color: 'red' | 'black' };

export interface EncounterEntry {
  tokenId: number;
  card: Card;
  /** Added mid-encounter — stays at the end until the next round's reshuffle. */
  pinned?: boolean;
}

export interface EncounterState {
  round: number;
  /** The token whose turn it is, or null before the first deal. */
  activeTokenId: number | null;
  /** Explicit, already-sorted initiative sequence. */
  order: EncounterEntry[];
}

export interface MapRecord {
  id: number;
  name: string;
  image_path: string;
  width_ft: number;
  height_ft: number;
  viewport_x: number;
  viewport_y: number;
  zoom: number;
  /** null when no walls/doors have been defined yet (treated as all-free). */
  overlay: MapOverlay | null;
  /** Cells ever seen by any PC. null when nothing explored yet (entire map is unseen). */
  explored: ExploredCells | null;
  /** null when no encounter is in progress. */
  encounter: EncounterState | null;
  created_at: string;
}

export interface MapSummary {
  id: number;
  name: string;
  created_at: string;
}

export type TokenType = 'pc' | 'npc';

export interface Token {
  id: number;
  map_id: number;
  type: TokenType;
  name: string;
  image_path: string | null;
  grid_x: number;
  grid_y: number;
  visibility_ft: number;
  status_text: string | null;
  is_downed: boolean;
  /** False when the token is in the legend/initiative panel only, not on the map. */
  is_placed: boolean;
}

export interface Viewport {
  x: number;
  y: number;
  zoom: number;
}

export type EditMode = 'tokens' | 'overlay';

export type OverlaySubMode = 'walls' | 'terrain';

export interface AppState {
  user: User | null;
  csrfToken: string | null;
  maps: MapSummary[];
  currentMap: MapRecord | null;
  tokens: Token[];
  viewport: Viewport;
  /** Set while a token is being dragged on the canvas — canvas hides the original and draws a ghost. */
  draggingTokenId: number | null;
  /** UI mode: 'tokens' for normal play, 'overlay' for editing walls/doors/terrain. */
  editMode: EditMode;
  /** Sub-mode within overlay editing: paint walls or terrain. */
  overlaySubMode: OverlaySubMode;
}

/** Shape of a backend error: { error: { code, message } }. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
  };
}

/** Thrown by the API client on non-2xx responses. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
